import { Link } from "react-router-dom";
import { ArrowRight, Calculator, Info } from "lucide-react";
import PageHero from "../components/PageHero";
import CTASection from "../components/CTASection";
import { Button } from "../components/ui/button";
import { ITIN_SERVICES, IMAGES } from "../data/content";

export default function ItinTax() {
  return (
    <div data-testid="itin-page">
      <PageHero
        eyebrow="ITIN & Tax"
        title="ITIN, Tax & Financial Start Services"
        lead="An Individual Taxpayer Identification Number opens doors: filing taxes, opening bank accounts, building a financial foundation. We help you understand the process and prepare accurate applications within our authorized scope."
        image={IMAGES.signing}
        alt="Preparing financial documents"
      />

      <section className="py-16 lg:py-24" data-testid="itin-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="h-11 w-11 rounded-xl bg-teal-light flex items-center justify-center mb-5">
                <Calculator className="h-5 w-5 text-teal" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-navy tracking-tight">What we help with</h2>
              <p className="mt-4 text-base text-mutedink leading-relaxed">
                From Form W-7 to your first bank account, we organize the steps and explain each one in plain language.
              </p>
              <div className="mt-6 bg-champagne-light border border-champagne/40 rounded-xl p-5 flex gap-3">
                <Info className="h-5 w-5 text-champagne-dark flex-shrink-0 mt-0.5" />
                <p className="text-sm text-ink/80 leading-relaxed">
                  Acceptance Agent services are offered only where IRS authorization has been granted. Tax-return preparation is provided only if properly authorized and registered. When you need a licensed tax professional, we refer you to one.
                </p>
              </div>
              <Button asChild className="mt-6 bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full" data-testid="get-itin-help-button">
                <Link to="/start">Get ITIN Help <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {ITIN_SERVICES.map((s) => (
                <div key={s} className="flex items-start gap-3 bg-surface border border-line rounded-lg p-4">
                  <span className="mt-1.5 h-2 w-2 rounded-full bg-teal flex-shrink-0" />
                  <span className="text-sm text-ink">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-sand/60" data-testid="financial-start">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-navy tracking-tight">Your financial start, in order</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-4 gap-4">
            {["ITIN or SSN", "Bank account", "Budget & savings", "Build credit"].map((step, i) => (
              <div key={step} className="bg-surface border border-line rounded-xl p-5">
                <div className="h-9 w-9 mx-auto rounded-full bg-sage-light flex items-center justify-center font-serif font-semibold text-sage mb-3">{i + 1}</div>
                <p className="text-sm font-medium text-navy">{step}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-mutedink">Each step builds on the last. We help you take them in the right order.</p>
        </div>
      </section>

      <CTASection title="Ready to organize your tax and financial start?" primaryLabel="Get ITIN Help" />
    </div>
  );
}
